import { isConsenthub } from '@idConnect/helpers/isConsenthub';
import { isConnectMessageType, unwrap } from './utils';
import { getVersions } from '@shared/helpers/getVersions';
import { handleDataClear } from '@utiqLoader/helpers/handleDataClear';
import { clearConsent, denyConsent } from './actions';
import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';

/* This whole function is a rancid tangle of complexity. There's probably a lot we could do to simplify it,
but we'll want to get some really good testing in place before we do.
*/
export const handleConsentRepromptRequirement: Maker<Ctx, Snap, null> = ({
  makeEffect,
}) => [
  makeEffect(
    'handle',
    (_, { snap, ctx, skip, pass }) => {
      if (!ctx.iframeMsgs[0]) return skip('!ctx.iframeMsgs[0]');
      const { msg } = ctx.iframeMsgs[0];
      if (!isConnectMessageType(msg)) return skip('!isConnectMessageType(msg)');
      if (msg.msgType !== 'GETDATA') return skip("msg.msgType !== 'GETDATA'");
      if (msg.status !== 'DONE') return skip("msg.status !== 'DONE'");
      if (!msg.body) return skip('!msg.body');

      if (
        JSON.stringify(msg.body.data) ===
        JSON.stringify(ctx.state.resolvingConnectId)
      ) {
        return skip(`msg is identical to stored data`);
      }

      if (
        ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
          ?.IDCONNECT_TYPE !== 'utiq'
      )
        return skip(
          "ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG ?.IDCONNECT_TYPE !== 'utiq'",
        );

      if (isConsenthub()) return skip('is consent hub');

      if (!ctx.window.Utiq.data?.popupContent?.version)
        return skip('no popupContent version');

      if (!isConnectMessageType(msg)) return skip('invalid message');

      const messageConnectId = msg.body.data;
      const localStorageIdConnectData = snap.localStorage.utiqPass;

      if (!messageConnectId) return skip('messageConnectId undefined');

      if (!localStorageIdConnectData)
        return skip('localStorageIdConnectData undefined');

      const isVersionInitiallyDefined =
        window.Utiq?.initialConsentVersionCookie !== '';

      if (window?.Utiq?.config?.getNewConsentBeforeExpiry === false)
        return skip(
          'window?.Utiq?.config?.getNewConsentBeforeExpiry === false',
        );

      if (Array.isArray(messageConnectId?.idGraph))
        return skip(`idGraph present. No repromet will be issued`);

      if (
        (localStorageIdConnectData &&
          localStorageIdConnectData.connectId &&
          Array.isArray(localStorageIdConnectData.connectId.idGraph) &&
          localStorageIdConnectData.connectId.idGraph.length > 0 &&
          localStorageIdConnectData.connectId.idGraph[0]?.category ===
            'mobile') ||
        snap.localStorage.utiqEligibility?.isEligible
      ) {
        const { majorVersion } = getVersions(
          ctx.window.Utiq.data?.popupContent.version,
        );

        const utiqConsentVersionCookieValue = snap.cookies.utiq_consent_version;

        // Only re-prompt if the version on the client is bellow version 3
        // and the version in the domain is grater or equal to 3
        // Also if !isVersionInitiallyDefined and Number(majorVersion) < 3
        const isVersionUndefinedAndDomainVersionLessThanThree =
          !isVersionInitiallyDefined && Number(majorVersion) < 3;
        const areVersionsTypeofNumber =
          typeof Number(majorVersion) === 'number' &&
          typeof Number(utiqConsentVersionCookieValue) === 'number';
        const isLastVersionForDomainAndClient =
          Number(majorVersion) >= 3 &&
          Number(utiqConsentVersionCookieValue) >= 3;
        const isLessThanThreeVersionForDomainAndClient =
          Number(majorVersion) < 3 && Number(utiqConsentVersionCookieValue) < 3;

        if (
          (isVersionInitiallyDefined ||
            isVersionUndefinedAndDomainVersionLessThanThree ||
            window.Utiq?.consentManagerShown) &&
          areVersionsTypeofNumber &&
          (isLastVersionForDomainAndClient ||
            isLessThanThreeVersionForDomainAndClient)
        ) {
          return skip(
            `Consent version cookie is not present, but the template data versionPrevious is ${ctx.window.Utiq.data?.popupContent.versionPrevious}. Continuing the flow without the reprompt...`,
          );
        }

        if (!ctx.window.Utiq.API) return skip(`ctx.window.Utiq.API missing`);

        if (!('handleDataClear' in ctx.window.Utiq.API))
          return skip(`handleDataClear missing`);

        if (!('showConsentManager' in ctx.window.Utiq.API))
          return skip(`"showConsentManagermissing`);

        return pass(
          true,
          `Client consent version ${utiqConsentVersionCookieValue} and domain consent version ${majorVersion}. Clearing the data and showing the consent manager...`,
        );
      }

      return skip(
        `Current utiqPass data is not suitable for consent reprompt. Continuing with the flow...`,
      );
    },

    (_, { ctx, snap }) => {
      ctx.iframeMsgs.shift();
      handleDataClear();
      ctx.times.__retriggerIdConnectFlow = snap.now;
      denyConsent(snap, ctx);
      ctx.times.handleConsentRepromptRequirement = snap.now;
      const Utiq = unwrap(ctx.window.Utiq);
      Utiq.initialConsentVersionCookie = undefined;
      Utiq.initialConsentVersionPreviousCookie = undefined;
      Utiq.API?.showConsentManager?.();
    },
  ),
];
